/**
 * Property Hub for Instructions (Pre-Sale)
 * Same layout as SalesPropertyPage but powered by property_instructions data
 * Route: /sales/property-hub/:instructionId
 */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Home, PoundSterling, Bed, Bath, Car, Square,
  MapPin, Calendar, User, Building2, FileText, Camera, 
  ChevronRight, Loader2, CheckCircle, Clock, Users, Briefcase,
  Image, Layout, Zap, Key, BookOpen, Eye, HandCoins, TrendingUp,
  Upload, X, Plus, ExternalLink, Save, Circle, MessageSquare, Send, Globe, Download
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { API } from "@/App";
import SendFeedbackModal from "@/components/feedback/SendFeedbackModal";


const InstructionHubPage = () => {
  const { instructionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [instruction, setInstruction] = useState(null);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [uploading, setUploading] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [sendingContract, setSendingContract] = useState(false);
  const [previewingContract, setPreviewingContract] = useState(false);
  const photoInputRef = useRef(null);
  const floorplanInputRef = useRef(null);
  const token = localStorage.getItem("token");
  
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = user?.role || "";
  
  // Check if user can send contracts (Admin, Director, Sales Agent)
  const canSendContract = ["admin", "director", "sales_agent"].includes(userRole);
  const isAdmin = userRole === "admin" || userRole === "director";
  
  // Contract form data
  const [contractData, setContractData] = useState({
    client_names: "",
    property_address: "",
    property_postcode: "",
    asking_price: 0,
    agency_type: "sole",
    keys_received: false,
    ownership_type: "sole",
    client_address: "",
    client_postcode: "",
    primary_contact_name: "",
    primary_contact_email: "",
    telephone: "",
    home_tel: "",
    work_tel: "",
    mobile: "",
    period_of_sole_agency: "12 weeks",
    fee_type: "percentage",
    fee_amount: 1.0,
    vat_applicable: true,
    minimum_fee: null,
    other_fees_amount: null,
    other_fees_description: ""
  });
  
  // Marketing form data
  const [marketingData, setMarketingData] = useState({
    summary: "",
    description: "",
    key_features: [],
    photos: [],
    floorplan_url: "",
    price_qualifier: "standard"
  });
  const [newFeature, setNewFeature] = useState("");

  const priceQualifierOptions = [
    { value: "standard", label: "Standard Price" },
    { value: "offers_over", label: "Offers Over" },
    { value: "guide_price", label: "Guide Price" },
    { value: "oiro", label: "Offers In The Region Of" },
  ];

  useEffect(() => {
    fetchInstruction();
  }, [instructionId]);

  const fetchInstruction = async () => {
    try {
      setLoading(true);
      // Try sales first, then lettings
      let res;
      let context = "sales";
      try {
        res = await axios.get(`${API}/instructions/sales/${instructionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {
        res = await axios.get(`${API}/instructions/lettings/${instructionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        context = "lettings";
      }
      
      const data = res.data;
      setInstruction({ ...data, _context: context });
      
      // Fetch seller/landlord info from applicant or instruction
      let foundSellerInfo = false;
      if (data.applicant_id) {
        try {
          const appRes = await axios.get(`${API}/sales/applicants/${data.applicant_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const app = appRes.data;
          if (app.email) {
            setSellerInfo({
              name: app.name || "Seller",
              email: app.email,
              type: context === "sales" ? "seller" : "landlord"
            });
            foundSellerInfo = true;
          }
        } catch (e) {
          // Applicant fetch failed, will try vendor_email below
        }
      }
      
      // Fallback to instruction's vendor info
      if (!foundSellerInfo && data.vendor_email) {
        setSellerInfo({
          name: data.vendor_name || "Seller",
          email: data.vendor_email,
          type: context === "sales" ? "seller" : "landlord"
        });
      }
      
      // Initialize marketing data
      setMarketingData({
        summary: data.summary || "",
        description: data.description || "",
        key_features: data.key_features || data.features || [],
        photos: data.photos || [],
        floorplan_url: data.floorplan_url || "",
        price_qualifier: data.price_qualifier || "standard"
      });
      
      // Initialize contract data from instruction
      const contract = data.contract || {};
      const fields = contract.fields || {};
      setContractData({
        client_names: fields.client_names || data.vendor_name || "",
        property_address: fields.property_address || data.full_address || "",
        property_postcode: fields.property_postcode || (data.address?.postcode || ""),
        asking_price: fields.asking_price || data.asking_price || 0,
        agency_type: fields.agency_type || "sole",
        keys_received: fields.keys_received || false,
        ownership_type: fields.ownership_type || "sole",
        client_address: fields.client_address || "",
        client_postcode: fields.client_postcode || "",
        primary_contact_name: fields.primary_contact_name || data.vendor_name || "",
        primary_contact_email: fields.primary_contact_email || data.vendor_email || "",
        telephone: fields.telephone || data.vendor_phone || "",
        home_tel: fields.home_tel || "",
        work_tel: fields.work_tel || "",
        mobile: fields.mobile || data.vendor_phone || "",
        period_of_sole_agency: fields.period_of_sole_agency || "12 weeks",
        fee_type: fields.fee_type || "percentage",
        fee_amount: fields.fee_amount || data.fee_amount || 1.0,
        vat_applicable: fields.vat_applicable !== false,
        minimum_fee: fields.minimum_fee || null,
        other_fees_amount: fields.other_fees_amount || null,
        other_fees_description: fields.other_fees_description || ""
      });
    } catch (error) {
      console.error("Error fetching instruction:", error);
      toast.error("Failed to load property");
    } finally {
      setLoading(false);
    }
  };
  
  // Contract functions
  const handleSaveContract = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/instructions/${instructionId}/contract`,
        { fields: contractData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contract details saved");
      fetchInstruction();
    } catch (error) {
      console.error("Error saving contract:", error);
      toast.error("Failed to save contract details");
    } finally {
      setSaving(false);
    }
  };
  
  const handlePreviewContract = async () => {
    setPreviewingContract(true);
    try {
      // Save contract first
      await axios.put(
        `${API}/instructions/${instructionId}/contract`,
        { fields: contractData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Download the preview document directly with authentication
      const response = await axios.get(
        `${API}/instructions/${instructionId}/contract/preview-html`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'  // Important: get as binary
        }
      );
      
      // Create download link for PDF
      const blob = new Blob([response.data], { 
        type: 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeAddress = (contractData?.property_address || instructionId).replace(/[,\/]/g, '').substring(0, 30);
      link.download = `Contract_Preview_${safeAddress}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Contract preview downloaded");
    } catch (error) {
      console.error("Error previewing contract:", error);
      toast.error(error.response?.data?.detail || "Failed to generate preview");
    } finally {
      setPreviewingContract(false);
    }
  };
  
  const handleSendContractNew = async (adminOverride = false) => {
    if (!instruction?.ids_uploaded && !adminOverride) {
      toast.error("IDs must be uploaded before sending the contract.");
      return;
    }
    
    setSendingContract(true);
    try {
      // Save contract first
      await axios.put(
        `${API}/instructions/${instructionId}/contract`,
        { fields: contractData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Send for signature
      const res = await axios.post(
        `${API}/instructions/${instructionId}/contract/send?admin_override=${adminOverride}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message || "Contract sent for signature");
      fetchInstruction();
    } catch (error) {
      console.error("Error sending contract:", error);
      toast.error(error.response?.data?.detail || "Failed to send contract");
    } finally {
      setSendingContract(false);
    }
  };

  const handleSaveMarketing = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/instructions/${instructionId}`,
        {
          summary: marketingData.summary,
          description: marketingData.description,
          key_features: marketingData.key_features,
          photos: marketingData.photos,
          floorplan_url: marketingData.floorplan_url,
          price_qualifier: marketingData.price_qualifier === "standard" ? null : marketingData.price_qualifier
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Marketing details saved");
      fetchInstruction();
    } catch (error) {
      console.error("Error saving marketing:", error);
      toast.error("Failed to save marketing details");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const res = await axios.post(
          `${API}/instructions/${instructionId}/upload-media?media_type=photo`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data"
            }
          }
        );
        
        if (res.data.photo) {
          setMarketingData(prev => ({
            ...prev,
            photos: [...prev.photos, res.data.photo]
          }));
        }
      } catch (error) {
        console.error("Error uploading photo:", error);
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    
    toast.success("Photos uploaded");
    setUploading(false);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const handleFloorplanUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(
        `${API}/instructions/${instructionId}/upload-media?media_type=floorplan`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      setMarketingData(prev => ({
        ...prev,
        floorplan_url: res.data.url
      }));
      toast.success("Floorplan uploaded");
    } catch (error) {
      console.error("Error uploading floorplan:", error);
      toast.error("Failed to upload floorplan");
    } finally {
      setUploading(false);
      if (floorplanInputRef.current) floorplanInputRef.current.value = "";
    }
  };

  const handleDeletePhoto = async (index) => {
    try {
      await axios.delete(
        `${API}/instructions/${instructionId}/photos/${index}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMarketingData(prev => ({
        ...prev,
        photos: prev.photos.filter((_, i) => i !== index)
      }));
      toast.success("Photo deleted");
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error("Failed to delete photo");
    }
  };

  const handleAddFeature = () => {
    if (!newFeature.trim()) return;
    if (marketingData.key_features.length >= 10) {
      toast.error("Maximum 10 key features allowed");
      return;
    }
    setMarketingData(prev => ({
      ...prev,
      key_features: [...prev.key_features, newFeature.trim()]
    }));
    setNewFeature("");
  };

  const handleRemoveFeature = (index) => {
    setMarketingData(prev => ({
      ...prev,
      key_features: prev.key_features.filter((_, i) => i !== index)
    }));
  };

  const handleTogglePortalLive = async () => {
    if (!instruction?.marketing_approved) {
      toast.error("Marketing must be approved before going live on portals");
      return;
    }
    
    setSaving(true);
    try {
      const newStatus = !instruction.portal_live;
      await axios.put(
        `${API}/instructions/${instructionId}`,
        { 
          portal_live: newStatus,
          portal_live_at: newStatus ? new Date().toISOString() : null 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(newStatus ? "Property is now live on portals!" : "Property removed from portals");
      fetchInstruction();
    } catch (error) {
      console.error("Error toggling portal status:", error);
      toast.error(error.response?.data?.detail || "Failed to update portal status");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkInstructed = async () => {
    try {
      const res = await axios.post(
        `${API}/instructions/${instruction.instruction_id}/mark-instructed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Marked as instructed - portal invite sent");
      fetchInstruction();
    } catch (error) {
      console.error("Error marking as instructed:", error);
      toast.error(error.response?.data?.detail || "Failed to mark as instructed");
    }
  };

  const handleSendMarketingDraft = async () => {
    try {
      const res = await axios.post(
        `${API}/instructions/${instruction.instruction_id}/send-marketing-draft`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Marketing draft sent for approval");
      fetchInstruction();
    } catch (error) {
      console.error("Error sending marketing draft:", error);
      toast.error(error.response?.data?.detail || "Failed to send marketing draft");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-slate-100 text-slate-700',
      'pending_approval': 'bg-amber-100 text-amber-700',
      'approved': 'bg-blue-100 text-blue-700',
      'live': 'bg-emerald-100 text-emerald-700',
      'sold': 'bg-purple-100 text-purple-700',
      'withdrawn': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  // Property Menu items (same as SalesPropertyPage)
  const propertyMenuItems = [
    { id: "dashboard", label: "Dashboard", icon: Home },
    { id: "details", label: "Property Details", icon: Building2 },
    { id: "contract", label: "Contract", icon: FileText },
    { id: "marketing", label: "Marketing Details", icon: TrendingUp },
    { id: "photos", label: "Photos", icon: Image },
    { id: "floorplans", label: "Floorplans", icon: Layout },
    { id: "epc", label: "EPC", icon: Zap },
    { id: "ids", label: "IDs", icon: Key },
    { id: "brochures", label: "Brochures", icon: BookOpen },
    { id: "documents", label: "Documents", icon: FileText },
  ];

  // Workflow items (instruction specific) - IDs must be step #1 and gate contract
  const workflowItems = [
    { 
      id: "ids_uploaded", 
      label: "IDs Uploaded", 
      done: !!instruction?.ids_uploaded || (Array.isArray(instruction?.id_documents) && instruction.id_documents.length >= 2)
    },
    { 
      id: "contract_accepted", 
      label: "Contract Accepted", 
      done: !!instruction?.contract_accepted || instruction?.contract?.status === "accepted"
    },
    { 
      id: "marketing_approved", 
      label: "Marketing Approved", 
      done: !!instruction?.marketing_approved
    },
    { 
      id: "portal_live", 
      label: "Live on Portals", 
      done: !!instruction?.portal_live || !!instruction?.portal_live_at 
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!instruction) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Home className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Property Not Found</h2>
            <p className="text-slate-500 mb-4">This property instruction could not be found.</p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div>
                <h1 className="text-lg font-semibold text-slate-800">{instruction.full_address}</h1>
                <p className="text-sm text-slate-500">Ref: {instruction.property_ref} • {instruction.type === "sales" ? "Sales" : "Lettings"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Mark as Instructed Button */}
              {canSendContract && instruction?.type === "sales" && !instruction?.portal_invited && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkInstructed}
                  data-testid="mark-instructed-btn"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Instructed
                </Button>
              )}
              
              {/* Send Marketing Draft Button */}
              {canSendContract && instruction?.type === "sales" && instruction?.portal_invited && !instruction?.marketing_approved && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendMarketingDraft}
                  data-testid="send-marketing-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Send Draft to Seller
                </Button>
              )}
              
              {sellerInfo?.email && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowFeedbackModal(true)}
                  data-testid="send-feedback-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Feedback
                </Button>
              )}
              <Badge className={`text-sm px-3 py-1 ${getStatusColor(instruction.status)}`}>
                {instruction.status?.replace("_", " ").toUpperCase()}
              </Badge>
              <Badge className="bg-emerald-100 text-emerald-700 text-lg px-4 py-1">
                {formatCurrency(instruction.asking_price || instruction.monthly_rent)}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Menu */}
          <div className="w-56 flex-shrink-0">
            <Card className="sticky top-24">
              <CardContent className="p-3">
                {/* Property Menu */}
                <div className="mb-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">Property</h3>
                  <nav className="space-y-0.5">
                    {propertyMenuItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => setActiveSection(item.id)}
                          data-testid={`menu-${item.id}`}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                            isActive
                              ? "bg-blue-50 text-blue-700 font-medium"
                              : "text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm">{item.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Workflow Progress */}
                <div className="border-t pt-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">Workflow</h3>
                  <div className="space-y-1 px-3">
                    {workflowItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-2 text-sm">
                        {item.done ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Circle className="w-4 h-4 text-slate-300" />
                        )}
                        <span className={item.done ? "text-slate-700" : "text-slate-400"}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Link to Sale if case exists */}
                {instruction.case_id && (
                  <div className="border-t pt-4 mt-4">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate(`/sales/property/${instruction.case_id}`)}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Go to Sale Progression
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Dashboard Section */}
            {activeSection === "dashboard" && (
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4">Property Overview</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <Bed className="w-4 h-4" />
                          <span className="text-sm">Bedrooms</span>
                        </div>
                        <p className="text-2xl font-semibold">{instruction.bedrooms || "-"}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <Bath className="w-4 h-4" />
                          <span className="text-sm">Bathrooms</span>
                        </div>
                        <p className="text-2xl font-semibold">{instruction.bathrooms || "-"}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <Building2 className="w-4 h-4" />
                          <span className="text-sm">Property Type</span>
                        </div>
                        <p className="text-lg font-semibold capitalize">{instruction.property_type?.replace("_", " ") || "-"}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-slate-500 mb-1">
                          <PoundSterling className="w-4 h-4" />
                          <span className="text-sm">{instruction.type === "sales" ? "Asking Price" : "Monthly Rent"}</span>
                        </div>
                        <p className="text-lg font-semibold">{formatCurrency(instruction.asking_price || instruction.monthly_rent)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Workflow Progress Card */}
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4">Instruction Progress</h2>
                    <div className="flex items-center justify-between">
                      {workflowItems.map((item, idx) => (
                        <div key={item.id} className="flex items-center">
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              item.done ? "bg-emerald-100" : "bg-slate-100"
                            }`}>
                              {item.done ? (
                                <CheckCircle className="w-5 h-5 text-emerald-600" />
                              ) : (
                                <Circle className="w-5 h-5 text-slate-300" />
                              )}
                            </div>
                            <span className={`text-xs mt-2 text-center max-w-[80px] ${item.done ? "text-slate-700" : "text-slate-400"}`}>
                              {item.label}
                            </span>
                          </div>
                          {idx < workflowItems.length - 1 && (
                            <div className={`w-16 h-0.5 mx-2 ${item.done ? "bg-emerald-300" : "bg-slate-200"}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Live on Portals Toggle */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Globe className="w-5 h-5 text-blue-600" />
                          Live on Portals
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {instruction?.portal_live 
                            ? `Listed on Rightmove, Zoopla since ${instruction.portal_live_at ? new Date(instruction.portal_live_at).toLocaleDateString('en-GB') : 'recently'}`
                            : "Property is not yet live on property portals"
                          }
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {!instruction?.marketing_approved && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            Marketing approval required
                          </Badge>
                        )}
                        <Button
                          variant={instruction?.portal_live ? "outline" : "default"}
                          onClick={handleTogglePortalLive}
                          disabled={!instruction?.marketing_approved || saving}
                          data-testid="toggle-portal-live"
                          className={instruction?.portal_live ? "border-red-300 text-red-600 hover:bg-red-50" : "bg-emerald-600 hover:bg-emerald-700"}
                        >
                          {saving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : instruction?.portal_live ? (
                            <X className="w-4 h-4 mr-2" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          {instruction?.portal_live ? "Remove from Portals" : "Go Live"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Property Details Section */}
            {activeSection === "details" && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4">Property Details</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <DetailItem label="Property Type" value={instruction.property_type?.replace("_", " ")} />
                    <DetailItem label="Bedrooms" value={instruction.bedrooms} />
                    <DetailItem label="Bathrooms" value={instruction.bathrooms} />
                    <DetailItem label="Receptions" value={instruction.receptions} />
                    <DetailItem label="Tenure" value={instruction.tenure} />
                    <DetailItem label="EPC Rating" value={instruction.epc_rating} />
                    <DetailItem label="Council Tax Band" value={instruction.council_tax_band} />
                    <DetailItem label="Postcode" value={instruction.postcode} />
                    {instruction.type === "sales" && (
                      <>
                        <DetailItem label="Asking Price" value={formatCurrency(instruction.asking_price)} />
                        <DetailItem label="Agency Type" value={instruction.agency_type} />
                        <DetailItem label="Fee" value={instruction.fee_percentage ? `${instruction.fee_percentage}%` : "-"} />
                      </>
                    )}
                    {instruction.type === "lettings" && (
                      <>
                        <DetailItem label="Monthly Rent" value={`${formatCurrency(instruction.monthly_rent)} pcm`} />
                        <DetailItem label="Management" value={instruction.management_type?.replace("_", " ")} />
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contract Section */}
            {activeSection === "contract" && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold">Agency Contract</h2>
                      <p className="text-sm text-muted-foreground">
                        Fill in the contract details, preview, then send for signature
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {instruction?.contract?.status && (
                        <Badge variant={
                          instruction.contract.status === "accepted" ? "success" :
                          instruction.contract.status === "sent" ? "warning" :
                          instruction.contract.status === "declined" ? "destructive" : "secondary"
                        }>
                          {instruction.contract.status.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* IDs Status Warning */}
                  {!instruction?.ids_uploaded && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-2 text-amber-800">
                        <Key className="w-5 h-5" />
                        <p className="font-medium">IDs Required</p>
                      </div>
                      <p className="text-sm text-amber-700 mt-1">
                        Seller must upload ID documents before the contract can be sent.
                        {isAdmin && " Admins can override this requirement."}
                      </p>
                    </div>
                  )}
                  
                  {/* Contract Form */}
                  <div className="space-y-6">
                    {/* Property Details */}
                    <div className="border-b pb-4">
                      <h3 className="font-medium mb-4">Property Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Property Address</Label>
                          <Input
                            value={contractData.property_address}
                            onChange={(e) => setContractData(prev => ({ ...prev, property_address: e.target.value }))}
                            placeholder="Full property address"
                            data-testid="contract-property-address"
                          />
                        </div>
                        <div>
                          <Label>Postcode</Label>
                          <Input
                            value={contractData.property_postcode}
                            onChange={(e) => setContractData(prev => ({ ...prev, property_postcode: e.target.value }))}
                            placeholder="Postcode"
                            data-testid="contract-property-postcode"
                          />
                        </div>
                        <div>
                          <Label>Asking Price (£)</Label>
                          <Input
                            type="number"
                            value={contractData.asking_price}
                            onChange={(e) => setContractData(prev => ({ ...prev, asking_price: parseFloat(e.target.value) || 0 }))}
                            placeholder="0"
                            data-testid="contract-asking-price"
                          />
                        </div>
                        <div>
                          <Label>Agency Type</Label>
                          <Select 
                            value={contractData.agency_type} 
                            onValueChange={(value) => setContractData(prev => ({ ...prev, agency_type: value }))}
                          >
                            <SelectTrigger data-testid="contract-agency-type">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sole">Sole Agency</SelectItem>
                              <SelectItem value="multi">Multi Agency</SelectItem>
                              <SelectItem value="joint_sole">Joint Sole Agency</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="keys_received"
                            checked={contractData.keys_received}
                            onChange={(e) => setContractData(prev => ({ ...prev, keys_received: e.target.checked }))}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="keys_received" className="mb-0">Keys Received</Label>
                        </div>
                        <div>
                          <Label>Ownership Type</Label>
                          <Select 
                            value={contractData.ownership_type} 
                            onValueChange={(value) => setContractData(prev => ({ ...prev, ownership_type: value }))}
                          >
                            <SelectTrigger data-testid="contract-ownership-type">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sole">Sole Ownership</SelectItem>
                              <SelectItem value="multiple">Multiple Ownership</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Client Details */}
                    <div className="border-b pb-4">
                      <h3 className="font-medium mb-4">Client Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Client Name(s)</Label>
                          <Input
                            value={contractData.client_names}
                            onChange={(e) => setContractData(prev => ({ ...prev, client_names: e.target.value }))}
                            placeholder="Full legal names"
                            data-testid="contract-client-names"
                          />
                        </div>
                        <div>
                          <Label>Primary Contact Email</Label>
                          <Input
                            type="email"
                            value={contractData.primary_contact_email}
                            onChange={(e) => setContractData(prev => ({ ...prev, primary_contact_email: e.target.value }))}
                            placeholder="email@example.com"
                            data-testid="contract-email"
                          />
                        </div>
                        <div>
                          <Label>Client Address</Label>
                          <Input
                            value={contractData.client_address}
                            onChange={(e) => setContractData(prev => ({ ...prev, client_address: e.target.value }))}
                            placeholder="Current address"
                          />
                        </div>
                        <div>
                          <Label>Client Postcode</Label>
                          <Input
                            value={contractData.client_postcode}
                            onChange={(e) => setContractData(prev => ({ ...prev, client_postcode: e.target.value }))}
                            placeholder="Postcode"
                          />
                        </div>
                        <div>
                          <Label>Mobile</Label>
                          <Input
                            value={contractData.mobile}
                            onChange={(e) => setContractData(prev => ({ ...prev, mobile: e.target.value }))}
                            placeholder="Mobile number"
                          />
                        </div>
                        <div>
                          <Label>Home Telephone</Label>
                          <Input
                            value={contractData.home_tel}
                            onChange={(e) => setContractData(prev => ({ ...prev, home_tel: e.target.value }))}
                            placeholder="Home number"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Terms */}
                    <div>
                      <h3 className="font-medium mb-4">Terms & Fees</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Period of Sole Agency</Label>
                          <Select 
                            value={contractData.period_of_sole_agency} 
                            onValueChange={(value) => setContractData(prev => ({ ...prev, period_of_sole_agency: value }))}
                          >
                            <SelectTrigger data-testid="contract-period">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="8 weeks">8 Weeks</SelectItem>
                              <SelectItem value="10 weeks">10 Weeks</SelectItem>
                              <SelectItem value="12 weeks">12 Weeks</SelectItem>
                              <SelectItem value="16 weeks">16 Weeks</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Fee Type</Label>
                          <Select 
                            value={contractData.fee_type} 
                            onValueChange={(value) => setContractData(prev => ({ ...prev, fee_type: value }))}
                          >
                            <SelectTrigger data-testid="contract-fee-type">
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">Percentage</SelectItem>
                              <SelectItem value="fixed">Fixed Fee</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>{contractData.fee_type === "percentage" ? "Fee (%)" : "Fee (£)"}</Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={contractData.fee_amount}
                            onChange={(e) => setContractData(prev => ({ ...prev, fee_amount: parseFloat(e.target.value) || 0 }))}
                            placeholder="0"
                            data-testid="contract-fee-amount"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="vat_applicable"
                            checked={contractData.vat_applicable}
                            onChange={(e) => setContractData(prev => ({ ...prev, vat_applicable: e.target.checked }))}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="vat_applicable" className="mb-0">VAT Applicable</Label>
                        </div>
                        <div>
                          <Label>Minimum Fee (£) - if applicable</Label>
                          <Input
                            type="number"
                            value={contractData.minimum_fee || ""}
                            onChange={(e) => setContractData(prev => ({ ...prev, minimum_fee: e.target.value ? parseFloat(e.target.value) : null }))}
                            placeholder="Leave blank if not applicable"
                            data-testid="contract-min-fee"
                          />
                        </div>
                      </div>
                      
                      {/* Other Fees */}
                      <div className="mt-4 pt-4 border-t border-dashed">
                        <h4 className="text-sm font-medium mb-3">Other Fees & Disbursements</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Amount (£)</Label>
                            <Input
                              type="number"
                              value={contractData.other_fees_amount || ""}
                              onChange={(e) => setContractData(prev => ({ ...prev, other_fees_amount: e.target.value ? parseFloat(e.target.value) : null }))}
                              placeholder="0"
                              data-testid="contract-other-fees"
                            />
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Input
                              value={contractData.other_fees_description}
                              onChange={(e) => setContractData(prev => ({ ...prev, other_fees_description: e.target.value }))}
                              placeholder="e.g., Premium portal listing, drone photography"
                              data-testid="contract-other-fees-desc"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between mt-8 pt-4 border-t">
                    <Button variant="outline" onClick={handleSaveContract} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Draft
                    </Button>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        onClick={handlePreviewContract}
                        disabled={previewingContract}
                        data-testid="preview-contract-btn"
                      >
                        {previewingContract ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Download Preview
                      </Button>
                      
                      {instruction?.ids_uploaded ? (
                        <Button 
                          onClick={() => handleSendContractNew(false)}
                          disabled={sendingContract || instruction?.contract?.status === "sent" || instruction?.contract?.status === "accepted"}
                          data-testid="send-contract-btn"
                        >
                          {sendingContract ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          {instruction?.contract?.status === "sent" ? "Awaiting Acceptance" : 
                           instruction?.contract?.status === "accepted" ? "Accepted" : "Send for Acceptance"}
                        </Button>
                      ) : isAdmin ? (
                        <Button 
                          variant="destructive"
                          onClick={() => handleSendContractNew(true)}
                          disabled={sendingContract}
                          data-testid="send-contract-override-btn"
                        >
                          {sendingContract ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                          Send (Override)
                        </Button>
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button disabled data-testid="send-contract-disabled">
                                  <Send className="w-4 h-4 mr-2" />
                                  Send for Acceptance
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>IDs must be uploaded first</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                  
                  {/* Contract Timeline */}
                  {instruction?.contract?.sent_at && (
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="font-medium mb-2">Contract Timeline</h4>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>Sent: {new Date(instruction.contract.sent_at).toLocaleString('en-GB')}</p>
                        {instruction.contract.accepted_at && (
                          <p className="text-green-600">Accepted: {new Date(instruction.contract.accepted_at).toLocaleString('en-GB')}</p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Marketing Details Section */}
            {activeSection === "marketing" && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Marketing Details</h2>
                    <Button onClick={handleSaveMarketing} disabled={saving} data-testid="save-marketing-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                  <div className="space-y-6">
                    {/* Marketing Price Options (Sales only) */}
                    {instruction?.type === "sales" && (
                      <div>
                        <Label>Marketing Price Option</Label>
                        <Select 
                          value={marketingData.price_qualifier} 
                          onValueChange={(value) => setMarketingData(prev => ({ ...prev, price_qualifier: value }))}
                        >
                          <SelectTrigger className="w-full mt-1" data-testid="price-qualifier-select">
                            <SelectValue placeholder="Select price type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {priceQualifierOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          How the price will appear on marketing materials and portals
                        </p>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="summary">Summary / Headline (max 160 chars)</Label>
                      <Input
                        id="summary"
                        value={marketingData.summary}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, summary: e.target.value }))}
                        placeholder="Brief headline for portal listings..."
                        maxLength={160}
                        data-testid="marketing-summary"
                      />
                      <p className="text-xs text-muted-foreground mt-1">{marketingData.summary.length}/160</p>
                    </div>
                    <div>
                      <Label htmlFor="description">Full Description</Label>
                      <Textarea
                        id="description"
                        value={marketingData.description}
                        onChange={(e) => setMarketingData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Full property description..."
                        rows={8}
                        data-testid="marketing-description"
                      />
                    </div>
                    <div>
                      <Label>Key Features (max 10)</Label>
                      <div className="flex gap-2 mt-2">
                        <Input
                          value={newFeature}
                          onChange={(e) => setNewFeature(e.target.value)}
                          placeholder="Add a key feature..."
                          onKeyPress={(e) => e.key === "Enter" && handleAddFeature()}
                        />
                        <Button type="button" onClick={handleAddFeature} disabled={marketingData.key_features.length >= 10}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <ul className="mt-3 space-y-2">
                        {marketingData.key_features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-md">
                            <span className="text-sm font-medium text-slate-600">{index + 1}.</span>
                            <span className="flex-1 text-sm">{feature}</span>
                            <Button variant="ghost" size="sm" onClick={() => handleRemoveFeature(index)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Photos Section */}
            {activeSection === "photos" && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Property Photos</h2>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">{marketingData.photos.length}/20 uploaded</span>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                      <Button onClick={() => photoInputRef.current?.click()} disabled={uploading} data-testid="upload-photos-btn">
                        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Upload Photos
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">Click on any slot to upload a photo. First photo will be the main image.</p>
                  
                  {/* 20 Photo Slots Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {Array.from({ length: 20 }).map((_, index) => {
                      const photo = marketingData.photos[index];
                      return (
                        <div 
                          key={index} 
                          className={`relative group aspect-[4/3] rounded-lg overflow-hidden border-2 ${
                            photo ? 'border-slate-200' : 'border-dashed border-slate-300 hover:border-blue-400 cursor-pointer'
                          } ${index === 0 && photo ? 'ring-2 ring-emerald-500' : ''}`}
                          onClick={() => !photo && photoInputRef.current?.click()}
                          data-testid={`photo-slot-${index + 1}`}
                        >
                          {photo ? (
                            <>
                              <img
                                src={`${API}${photo.url}`}
                                alt={`Property photo ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />
                              <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                {index === 0 ? 'Main' : `#${index + 1}`}
                              </div>
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => { e.stopPropagation(); handleDeletePhoto(index); }}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          ) : (
                            <div className="w-full h-full bg-slate-50 flex flex-col items-center justify-center">
                              <Camera className="w-6 h-6 text-slate-300 mb-1" />
                              <span className="text-xs text-slate-400">{index === 0 ? 'Main' : index + 1}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Floorplans Section */}
            {activeSection === "floorplans" && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Floorplans</h2>
                    <div>
                      <input
                        ref={floorplanInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFloorplanUpload}
                        className="hidden"
                      />
                      <Button onClick={() => floorplanInputRef.current?.click()} disabled={uploading} data-testid="upload-floorplan-btn">
                        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        {marketingData.floorplan_url ? "Replace Floorplan" : "Upload Floorplan"}
                      </Button>
                    </div>
                  </div>
                  {marketingData.floorplan_url ? (
                    <div className="max-w-2xl mx-auto">
                      <div className="aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden">
                        <img
                          src={`${API}${marketingData.floorplan_url}`}
                          alt="Floorplan"
                          className="w-full h-full object-contain"
                          onError={(e) => { e.target.src = '/placeholder-image.png'; }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                      <Layout className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-muted-foreground">No floorplan uploaded yet</p>
                      <Button variant="outline" className="mt-3" onClick={() => floorplanInputRef.current?.click()}>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Floorplan
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* EPC Section */}
            {activeSection === "epc" && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4">Energy Performance Certificate</h2>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <DetailItem label="EPC Rating" value={instruction.epc_rating || "Not provided"} />
                    <DetailItem label="EPC Reference" value={instruction.epc_reference || "Not provided"} />
                  </div>
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <Zap className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-muted-foreground">EPC document upload coming soon</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* IDs Section */}
            {activeSection === "ids" && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">ID Verification</h2>
                    <div className="flex items-center gap-3">
                      {instruction.ids_uploaded ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          IDs Complete
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          <Clock className="w-4 h-4 mr-1" />
                          Awaiting IDs
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {instruction.portal_invited && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        <strong>Portal invite sent to:</strong> {instruction.portal_invited_to}
                        <br />
                        <span className="text-blue-600">Seller can upload IDs through their portal</span>
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className={`p-4 rounded-lg border-2 ${
                      instruction.id_documents?.some(d => ["passport", "driving_licence"].includes(d.type))
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-slate-200 bg-slate-50"
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {instruction.id_documents?.some(d => ["passport", "driving_licence"].includes(d.type)) ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300" />
                        )}
                        <span className="font-medium">Photo ID</span>
                      </div>
                      <p className="text-sm text-slate-600">Passport or Driving Licence</p>
                    </div>
                    
                    <div className={`p-4 rounded-lg border-2 ${
                      instruction.id_documents?.some(d => ["proof_of_address", "utility_bill"].includes(d.type))
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-slate-200 bg-slate-50"
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {instruction.id_documents?.some(d => ["proof_of_address", "utility_bill"].includes(d.type)) ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300" />
                        )}
                        <span className="font-medium">Proof of Address</span>
                      </div>
                      <p className="text-sm text-slate-600">Utility bill or bank statement</p>
                    </div>
                  </div>
                  
                  {instruction.id_documents?.length > 0 ? (
                    <div>
                      <h3 className="text-sm font-medium text-slate-700 mb-3">Uploaded Documents</h3>
                      <div className="space-y-2">
                        {instruction.id_documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-slate-400" />
                              <div>
                                <p className="text-sm font-medium">{doc.original_name || doc.filename}</p>
                                <p className="text-xs text-slate-500">
                                  {doc.type.replace("_", " ")} • Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => window.open(`${API}${doc.url}`, '_blank')}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed">
                      <Key className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-muted-foreground">No ID documents uploaded yet</p>
                      <p className="text-sm text-slate-400 mt-1">Seller will upload IDs through their portal</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Brochures Section */}
            {activeSection === "brochures" && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4">Property Brochures</h2>
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-muted-foreground">Brochure generation coming soon</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Documents Section */}
            {activeSection === "documents" && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-4">Documents</h2>
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-muted-foreground">Document management coming soon</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Send Feedback Modal */}
      <SendFeedbackModal
        open={showFeedbackModal}
        onOpenChange={setShowFeedbackModal}
        context={instruction?._context || "sales"}
        propertyId={instruction?.property_id}
        instructionId={instructionId}
        recipientEmail={sellerInfo?.email}
        recipientType={sellerInfo?.type || "seller"}
        recipientName={sellerInfo?.name || "Seller"}
      />
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <div>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="font-medium capitalize">{value || "-"}</p>
  </div>
);

export default InstructionHubPage;
